import {isFeatureEnabled} from '@github-ui/feature-flags'

/**
 * Generates a task path for a given pull request global ID.
 * @param globalId - The global ID of the pull request.
 * @param sessionId - The ID of the session to be focused on page load.
 * @returns The task path URL, or an empty string if globalId is empty.
 */
export const generateTaskPath = (globalId: string, sessionId?: string) => {
  if (!globalId) {
    return ''
  }

  let taskPath = `/copilot/tasks/pull/${encodeURIComponent(globalId)}`
  if (sessionId) {
    taskPath += `?session_id=${encodeURIComponent(sessionId)}`
  }

  return taskPath
}

/**
 * Generates a task path for a given pull request global ID within a specific repository context.
 * @param globalId - The global ID of the pull request.
 * @param repositoryNwo - The repository's name with owner (e.g., "owner/repo").
 * @param pullRequestNumber - The pull request number.
 * @param sessionId - The ID of the session to be focused on page load.
 *
 * @returns The task path URL, or an empty string if globalId is empty.
 */
export const generateTaskPathForRepo = (
  globalId: string,
  repositoryNwo: string,
  pullRequestNumber: number,
  sessionId?: string,
) => {
  if (!globalId) {
    return ''
  }

  let taskPath = `/copilot/tasks/pull/${encodeURIComponent(globalId)}`

  if (repositoryNwo && pullRequestNumber && shouldLinkToRepoTaskView()) {
    taskPath = `/${repositoryNwo}/agents/pull/${encodeURIComponent(pullRequestNumber)}`
  }

  if (sessionId) {
    taskPath += `?session_id=${encodeURIComponent(sessionId)}`
  }

  return taskPath
}

/**
 * Signifies if we should link to the repository-based task view instead of the hyperspace view.
 * @returns True if the repository-based task view should be used, false otherwise.
 */
export const shouldLinkToRepoTaskView = () => {
  return (
    isFeatureEnabled('copilot_mission_control_link_to_sessions_in_repo') &&
    !isFeatureEnabled('copilot_mission_control_link_to_hyperspace')
  )
}
